<template>
    <modal
        id="users-bind-committee"
        size="lg"
        :title="$t('people.linkCommittees')"
        :okTitle="getOkTitle"
        :okDisabled="false"
        :ok-only="!canCurrentUserBindUsersToCommittee"
        :ok-variant="getVariantColor"
        :cancelTitle="$t('modal.cancel')"
        :cancelDisabled="false"
        :disabled="false"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        isModalRoute
        @okHandler="onSubmit"
    >
        <b-container fluid v-if="canCurrentUserBindUsersToCommittee">
            <b-row>
                <b-col>
                    <h5>
                        <b-badge class="p-2" variant="primary">
                            {{
                                $t(
                                    'usersBindCommittee.messages.youAreLinking',
                                    {
                                        users: users.length,
                                    }
                                )
                            }}
                        </b-badge>
                    </h5>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <div class="mb-2">
                        <small class="text-muted">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>
                                {{
                                    $t(
                                        'usersBindCommittee.messages.modalExplanation'
                                    )
                                }}
                            </strong>
                        </small>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <committee
                        :value.sync="$v.form.committee.$model"
                        :state="checkState('committee')"
                    >
                        {{ $t('form.validations.required') }}
                    </committee>
                </b-col>
            </b-row>
        </b-container>
        <b-container v-else>
            <div
                role="alert"
                aria-live="polite"
                aria-atomic="true"
                class="alert alert-info"
            >
                {{
                    $t(
                        'usersBulkEdit.messages.userWithoutPermissionToBindUserToCommittee'
                    )
                }}
            </div>
        </b-container>
    </modal>
</template>

<script>
import Modal from '@/views/components/modal'
import i18n from '@/i18n'
import Committee from '@/views/components/fields/Committee'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import Swal from 'sweetalert2'
import api from '@/services/api'

export default {
    name: 'BindCommittee',
    components: {
        Committee,
        Modal,
    },
    props: ['users'],
    data() {
        return {
            form: {
                committee: null,
            },
        }
    },
    mixins: [validationMixin],
    validations: {
        form: {
            committee: {
                required,
            },
        },
    },
    methods: {
        onSubmit(event) {
            event.preventDefault()
            if (!this.canCurrentUserBindUsersToCommittee) {
                return this.$router.back(-1)
            }

            if (!this.validate()) {
                return
            }

            Swal.queue([
                {
                    type: 'warning',
                    title: i18n.t('notification.areYouSure'),
                    text: i18n.t('notification.updateUsersCommittee'),
                    confirmButtonText: i18n.t('notification.yes'),
                    cancelButtonText: i18n.t('notification.no'),
                    showLoaderOnConfirm: true,
                    showCancelButton: true,
                    preConfirm: () => {
                        let params = {
                            committee: this.form.committee.value,
                            users: this.users,
                        }
                        return api
                            .post(
                                'admin/committees/bind-users-to-the-committee',
                                params
                            )
                            .then(() => {
                                Swal.insertQueueStep({
                                    type: 'success',
                                    title: i18n.t(
                                        'notification.requestSentSuccessfully'
                                    ),
                                    text: i18n.t(
                                        'notification.usersBulkEditRequestSuccessfullySent'
                                    ),
                                })
                                this.$router.back(-1)
                            })
                            .catch(error => {
                                Swal.insertQueueStep({
                                    title: i18n.t(
                                        'notification.somethingWentWrong'
                                    ),
                                    type: 'error',
                                    text: i18n.t(
                                        'notification.usersBulkEditRequestFailed'
                                    ),
                                })
                                console.log(error)
                            })
                    },
                },
            ])
        },
        validate() {
            this.$v.$touch()
            return this.formIsValid
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        currentUserhasPermission(
            availableRoles,
            availableModules,
            apiOnly = false
        ) {
            let hasModule = true
            let hasRole = true

            if (availableRoles.length) {
                hasRole =
                    availableRoles.filter(role => {
                        return this.currentUser.has_roles.includes(role)
                    }).length > 0
            }

            if (availableModules.length) {
                hasModule =
                    availableModules.filter(mod => {
                        return this.currentUser.has_modules.includes(mod)
                    }).length > 0
            }

            if (apiOnly && !this.currentUserHasApiModule) {
                return false
            }
            return hasModule && hasRole
        },
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        currentUserHasApiModule() {
            return this.currentUser.company.url_prefix != null
        },
        canCurrentUserBindUsersToCommittee() {
            const availableModules = []
            const availableRoles = [
                'local_administrator',
                'administrator',
                'is_target_master',
                'is_values_master',
            ]
            return this.currentUserhasPermission(
                availableRoles,
                availableModules
            )
        },
        getOkTitle() {
            return this.canCurrentUserBindUsersToCommittee
                ? i18n.t('modal.save')
                : i18n.t('modal.close')
        },
        getVariantColor() {
            return this.canCurrentUserBindUsersToCommittee ? 'primary' : 'light'
        },
        formIsValid() {
            return !this.$v.form.$anyError
        },
        committeeState() {
            return !this.$v.form.committee.$dirty
                ? null
                : !this.$v.form.committee.$invalid
        },
    },
}
</script>
